/* global fetchProjectPlanReportUrl exportProjectPlanReportUrl downloadProjectPlanReportUrl checkProjectPlanReportStatusUrl */
/* global projectPlanScopeOptions projectPlanScopeEntityOptions projectPlanCategoryOptions projectPlanOwnerOptions projectPlanPositionOptions projectPlanStatusOptions projectPlanTypeOptions channelName */
/* global projectPlanInitialCriteria projectPlanSaveUrl projectPlanReportKey */
/* global projectPlanEmailNowUrl projectPlanScheduleStoreUrl projectPlanSchedulePreviewUrl projectPlanScheduleDefaultTimezone projectPlanSupportedFormats projectPlanRecipientOptions */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import EmailReportNowModal from '../unified/EmailReportNowModal';
import ScheduleReportModal from '../unified/ScheduleReportModal';

import { ProjectPlanReportProvider, useProjectPlanReportContext } from './ProjectPlanReportContext';
import ProjectPlanReportCriteria from './ProjectPlanReportCriteria';
import ProjectPlanReportTable from './ProjectPlanReportTable';

import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import SectionTile from 'CommonComponents/SectionTile';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

/**
 * Project Plan report builder (IB-671 + IB-672 save-as).
 *
 * Mounts into the #ProjectPlanReport div on the report-centre builder page.
 * The criteria controls live in a collapsible tile above the live results
 * table; both read/write the shared report context, which auto-fetches the
 * on-screen data on criteria change and drives the Excel export flow. A
 * "Save this view" control (IB-672) persists the current criteria as a personal
 * saved report that then appears in the module gallery.
 */
const ProjectPlanReport = () => {
    const [criteriaCollapsed, setCriteriaCollapsed] = useState(false);

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary tw-space-y-7">
            <div className="tw-flex-initial tw-flex tw-items-center tw-justify-between tw-gap-3">
                <h2 className="tw-text-lg tw-font-semibold tw-text-charcoal tw-m-0">{$t('Project Plan')}</h2>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <DeliveryActions />
                    <SaveReportControl />
                </div>
            </div>

            <div className="tw-flex-initial">
                <SectionTile
                    flat
                    header={$t('Report Criteria')}
                    collapsed={criteriaCollapsed}
                    onToggle={() => setCriteriaCollapsed(!criteriaCollapsed)}
                    visibly
                >
                    <ProjectPlanReportCriteria />
                </SectionTile>
            </div>

            <ProjectPlanReportTable />
        </div>
    );
};

/**
 * Header delivery actions (IB-673 phase 6) operating on the builder's CURRENT
 * criteria: Download PDF (the export flow with format=pdf), an ad-hoc "Email now"
 * send, and Schedule. The two modals are the generic report-centre rails
 * (unified/EmailReportNowModal + unified/ScheduleReportModal); ProjectPlan is
 * personalisable, so scheduling exposes the per-recipient toggle. Rendered inside
 * the provider so it reads the live criteria and the injected delivery config.
 */
const DeliveryActions = () => {
    const {
        criteria,
        exportReport,
        exporting,
        reportKey,
        supportedFormats,
        recipientOptions,
        emailNowUrl,
        scheduleStoreUrl,
        schedulePreviewUrl,
        scheduleDefaultTimezone,
    } = useProjectPlanReportContext();

    const emailModal = useModal();
    const scheduleModal = useModal();

    // Bump per open so each modal resets its form (mirrors ReportParameterForm).
    const [emailNonce, setEmailNonce] = useState(0);
    const [scheduleNonce, setScheduleNonce] = useState(0);

    if (!reportKey) {
        return null;
    }

    // The rails' modals key a report by { type, name, supportedFormats };
    // ProjectPlan drives its own builder, so the name is fixed.
    const report = { type: reportKey, name: $t('Project Plan'), supportedFormats };

    const openEmail = () => {
        setEmailNonce((nonce) => nonce + 1);
        emailModal.show();
    };

    const openSchedule = () => {
        setScheduleNonce((nonce) => nonce + 1);
        scheduleModal.show();
    };

    return (
        <>
            <Button variant="secondary" small type="button" disabled={exporting} onClick={() => exportReport('pdf').catch(() => {})}>
                {$t('Download PDF')}
            </Button>
            {emailNowUrl ? (
                <Button variant="secondary" small type="button" onClick={openEmail}>
                    {$t('Email now')}
                </Button>
            ) : null}
            {scheduleStoreUrl ? (
                <Button variant="secondary" small type="button" onClick={openSchedule}>
                    {$t('Schedule')}
                </Button>
            ) : null}

            <EmailReportNowModal
                modal={emailModal}
                nonce={emailNonce}
                reportKey={reportKey}
                reportName={$t('Project Plan')}
                supportedFormats={supportedFormats}
                recipientOptions={recipientOptions}
                parameters={criteria}
                storeUrl={emailNowUrl}
            />

            <ScheduleReportModal
                modal={scheduleModal}
                mode="create"
                nonce={scheduleNonce}
                report={report}
                parameters={criteria}
                defaultTimezone={scheduleDefaultTimezone}
                storeUrl={scheduleStoreUrl}
                previewUrl={schedulePreviewUrl}
                personalisable
                userOptions={recipientOptions}
                onSaved={() => scheduleModal.hide()}
            />
        </>
    );
};

/**
 * "Save this view" — persists the builder's current criteria as a personal
 * ReportFilterSet (report_key = ProjectPlan) via the shared filter-set store
 * endpoint. Rendered inside the provider so it can read the live criteria.
 */
const SaveReportControl = () => {
    const { criteria } = useProjectPlanReportContext();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    const saveUrl = typeof projectPlanSaveUrl !== 'undefined' ? projectPlanSaveUrl : null;
    const reportKey = typeof projectPlanReportKey !== 'undefined' ? projectPlanReportKey : null;

    if (!saveUrl || !reportKey) {
        return null;
    }

    const save = () => {
        const trimmed = name.trim();

        if (trimmed === '' || saving) {
            return;
        }

        setSaving(true);

        apiClient
            .post(saveUrl, {
                report_key: reportKey,
                name: trimmed,
                parameters: criteria,
                is_shared: false,
            })
            .then(() => {
                toast.success($t('Report saved. It is now in your Project Management gallery.'));
                setOpen(false);
                setName('');
            })
            .catch(() => {
                toast.error($t('Failed to save the report. Please try again later.'));
            })
            .finally(() => setSaving(false));
    };

    return (
        <>
            <Button variant="secondary" small type="button" onClick={() => setOpen(true)}>
                {$t('Save this view')}
            </Button>

            {open ? (
                <div
                    className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-charcoal/40 tw-p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label={$t('Save this view')}
                >
                    <div className="tw-w-full tw-max-w-md tw-rounded-xl tw-bg-white tw-p-5 tw-shadow-primary">
                        <h3 className="tw-text-base tw-font-semibold tw-text-charcoal tw-mb-3">{$t('Save this view')}</h3>
                        <label className="tw-block tw-text-sm tw-text-charcoal-75 tw-mb-1" htmlFor="pp-save-name">
                            {$t('Report name')}
                        </label>
                        <input
                            id="pp-save-name"
                            type="text"
                            className="tw-w-full tw-rounded-lg tw-border tw-border-grey tw-px-3 tw-py-2 tw-text-sm"
                            value={name}
                            maxLength={255}
                            autoFocus
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    save();
                                }
                            }}
                        />
                        <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-2">
                            <Button variant="secondary" small type="button" disabled={saving} onClick={() => setOpen(false)}>
                                {$t('Cancel')}
                            </Button>
                            <Button variant="primary" small type="button" disabled={saving || name.trim() === ''} onClick={save}>
                                {saving ? $t('Saving…') : $t('Save')}
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default ProjectPlanReport;

const rootEl = document.getElementById('ProjectPlanReport');

if (rootEl) {
    ReactDOM.render(
        <App>
            <ProjectPlanReportProvider
                fetchReportUrl={fetchProjectPlanReportUrl}
                exportReportUrl={exportProjectPlanReportUrl}
                downloadReportUrl={downloadProjectPlanReportUrl}
                checkStatusUrl={checkProjectPlanReportStatusUrl}
                scopeOptions={projectPlanScopeOptions}
                scopeEntityOptions={projectPlanScopeEntityOptions}
                categoryOptions={projectPlanCategoryOptions}
                ownerOptions={projectPlanOwnerOptions}
                positionOptions={projectPlanPositionOptions}
                statusOptions={projectPlanStatusOptions}
                typeOptions={projectPlanTypeOptions}
                channelName={channelName}
                initialCriteria={typeof projectPlanInitialCriteria !== 'undefined' ? projectPlanInitialCriteria : {}}
                reportKey={typeof projectPlanReportKey !== 'undefined' ? projectPlanReportKey : null}
                supportedFormats={typeof projectPlanSupportedFormats !== 'undefined' ? projectPlanSupportedFormats : ['xlsx']}
                recipientOptions={typeof projectPlanRecipientOptions !== 'undefined' ? projectPlanRecipientOptions : []}
                emailNowUrl={typeof projectPlanEmailNowUrl !== 'undefined' ? projectPlanEmailNowUrl : null}
                scheduleStoreUrl={typeof projectPlanScheduleStoreUrl !== 'undefined' ? projectPlanScheduleStoreUrl : null}
                schedulePreviewUrl={typeof projectPlanSchedulePreviewUrl !== 'undefined' ? projectPlanSchedulePreviewUrl : null}
                scheduleDefaultTimezone={typeof projectPlanScheduleDefaultTimezone !== 'undefined' ? projectPlanScheduleDefaultTimezone : 'UTC'}
            >
                <ProjectPlanReport />
            </ProjectPlanReportProvider>
        </App>,
        rootEl,
    );
}
